import config from '@config'
import { Image, Link } from '@utils/cook/types'
import { type FmtCompetitionEntityIdsSortedSkeleton } from '@hooks/useFmtCompetitionEntityIdsSorted'

export type EntityType = 'competitor' | 'competition' | 'default'

export type RelatedEntity = {
  entityId: string
  entityType: EntityType
  title: string
  link: Link
}

export interface SportEntityType {
  entityId: string
  entityType: EntityType
  title: string
  link: Link
  isFollowingEnabled?: boolean
  sportradarId?: string
  abbreviation?: string
  image: Image<'free'>
  backgroundImage: Image<'16_9'>
  relatedEntities?: RelatedEntity[]
  subtitle?: string
  order: number
}

export type SportEntitiesData = SportEntityType[]

export type FetchSportEntities = (options: {
  isServerRequest?: boolean
  fmtCompetitionEntityIdsSorted: FmtCompetitionEntityIdsSortedSkeleton
}) => Promise<FormattedSportEntitiesData | undefined>

export interface FormattedSportEntitiesData {
  entitiesRegistry: Record<string, SportEntityType>
  entitiesOrderedList: string[]
  followableEntitiesOrderedList: string[]
  entitiesByTypeOrderedList: Partial<Record<EntityType, string[]>>
  followableEntitiesByTypeOrderedList: Partial<Record<EntityType, string[]>>
  entityToSportradarIdMap: Record<string, string>
  sportradarIdToEntityMap: Record<string, string>
  followableCompetitorsByCompetitionOrderedList: Record<string, string[]>
  followableCompetitorsWithoutCompetitionOrderedList: string[]
}

export type FormatSportEntities = (options: {
  sportEntities: SportEntitiesData
  fmtCompetitionEntityIdsSorted: FmtCompetitionEntityIdsSortedSkeleton
}) => FormattedSportEntitiesData

const {
  entities: { sportEntitiesBaseUrl },
} = config

const formatSportEntities: FormatSportEntities = ({
  sportEntities,
  fmtCompetitionEntityIdsSorted,
}): FormattedSportEntitiesData => {
  return sportEntities.reduce(
    (acc, entity) => {
      acc.entitiesRegistry[entity.entityId] = entity
      acc.entitiesOrderedList.push(entity.entityId)

      if (!Array.isArray(acc.entitiesByTypeOrderedList[entity.entityType])) {
        acc.entitiesByTypeOrderedList[entity.entityType] = []
      }

      ;(acc.entitiesByTypeOrderedList[entity.entityType] as string[]).push(
        entity.entityId,
      )

      if (entity.entityType === 'competitor' && entity.isFollowingEnabled) {
        if (Array.isArray(entity.relatedEntities)) {
          entity.relatedEntities.forEach((relatedEntity) => {
            if (
              !Array.isArray(
                acc.followableCompetitorsByCompetitionOrderedList[
                  relatedEntity.entityId
                ],
              )
            ) {
              acc.followableCompetitorsByCompetitionOrderedList[
                relatedEntity.entityId
              ] = []
            }

            ;(
              acc.followableCompetitorsByCompetitionOrderedList[
                relatedEntity.entityId
              ] as string[]
            ).push(entity.entityId)
          })

          if (
            !entity.relatedEntities.some((relatedEntity) => {
              return fmtCompetitionEntityIdsSorted.includes(
                relatedEntity.entityId,
              )
            })
          ) {
            acc.followableCompetitorsWithoutCompetitionOrderedList.push(
              entity.entityId,
            )
          }
        } else {
          acc.followableCompetitorsWithoutCompetitionOrderedList.push(
            entity.entityId,
          )
        }
      }

      if (entity.isFollowingEnabled) {
        acc.followableEntitiesOrderedList.push(entity.entityId)

        if (
          !Array.isArray(
            acc.followableEntitiesByTypeOrderedList[entity.entityType],
          )
        ) {
          acc.followableEntitiesByTypeOrderedList[entity.entityType] = []
        }

        ;(
          acc.followableEntitiesByTypeOrderedList[entity.entityType] as string[]
        ).push(entity.entityId)
      }

      if (entity.sportradarId) {
        acc.entityToSportradarIdMap[entity.entityId] = entity.sportradarId
        acc.sportradarIdToEntityMap[entity.sportradarId] = entity.entityId
      }
      return acc
    },
    {
      entitiesRegistry: {},
      entitiesOrderedList: [],
      followableEntitiesOrderedList: [],
      entitiesByTypeOrderedList: {},
      followableEntitiesByTypeOrderedList: {},
      entityToSportradarIdMap: {},
      sportradarIdToEntityMap: {},
      followableCompetitorsByCompetitionOrderedList: {},
      followableCompetitorsWithoutCompetitionOrderedList: [],
    } as FormattedSportEntitiesData,
  )
}

const fetchSportEntities: FetchSportEntities = async ({
  isServerRequest,
  fmtCompetitionEntityIdsSorted,
}) => {
  const sportEntitiesRequestURL = new URL(sportEntitiesBaseUrl)
  const sportEntitiesRequestHeaders: Partial<Record<'User-Agent', 'next'>> = {}

  sportEntitiesRequestURL.searchParams.set('size', '50')

  if (isServerRequest) {
    sportEntitiesRequestHeaders['User-Agent'] = 'next'
  }

  const initialResult = await (
    await fetch(sportEntitiesRequestURL, {
      headers: sportEntitiesRequestHeaders,
    })
  ).json()

  if (!Array.isArray(initialResult?.content)) {
    return
  }

  if (initialResult.total_pages < 2) {
    return formatSportEntities({
      sportEntities: initialResult.contents,
      fmtCompetitionEntityIdsSorted,
    })
  }

  const promiseArray = []

  for (let i = 1; i < initialResult.total_pages; i++) {
    const nextPageUrl = new URL(sportEntitiesBaseUrl)
    nextPageUrl.searchParams.set('page', String(i))
    promiseArray.push(
      fetch(nextPageUrl, {
        headers: sportEntitiesRequestHeaders,
      }).then((res) => res.json()),
    )
  }

  const restOfPages = await Promise.all(promiseArray)

  const combinedContent = restOfPages.reduce(
    (acc, page) => {
      if (page?.content && Array.isArray(page.content)) {
        acc.push(...page.content)
      }

      return acc
    },
    [...initialResult.content],
  )

  return formatSportEntities({
    sportEntities: combinedContent,
    fmtCompetitionEntityIdsSorted,
  })
}

const isSameSportEntity = (
  sportEntities: FormattedSportEntitiesData | undefined,
  entityId: string,
  sportradarId: string,
): boolean =>
  !sportEntities
    ? false
    : sportEntities.entityToSportradarIdMap[entityId] === sportradarId

const getSportEntityById = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
  entityId: string,
): SportEntityType | undefined => {
  const sportEntity = sportEntities?.entitiesRegistry[entityId]

  if (!sportEntity) {
    return
  }

  return {
    ...sportEntity,
    ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
  }
}

const getSportEntityBySportradarId = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
  sportradarId: string,
): SportEntityType | undefined => {
  const sportEntity =
    sportEntities?.entitiesRegistry[
      sportEntities?.sportradarIdToEntityMap[sportradarId]
    ]

  if (!sportEntity) {
    return
  }

  return {
    ...sportEntity,
    ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
  }
}

const getSportEntitiesList = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
  options?: { onlyEntityType?: EntityType; onlyIsFollowable?: boolean },
): SportEntitiesData => {
  const { onlyEntityType, onlyIsFollowable } = options ?? {
    onlyIsFollowable: false,
  }

  if (!sportEntities) {
    return []
  }

  if (onlyIsFollowable && !isFollowingEnabled) {
    return []
  }

  const prefilteredResult =
    sportEntities[
      `${onlyIsFollowable ? 'followableE' : 'e'}ntities${onlyEntityType ? 'ByType' : ''}OrderedList`
    ]

  let finalEntityIdList: string[] = []

  if (onlyEntityType && !Array.isArray(prefilteredResult)) {
    finalEntityIdList = prefilteredResult[onlyEntityType] ?? []
  } else if (Array.isArray(prefilteredResult)) {
    finalEntityIdList = prefilteredResult
  }

  return (
    finalEntityIdList.map((entityId) => {
      const sportEntity = sportEntities?.entitiesRegistry[entityId]

      return {
        ...sportEntity,
        ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
      }
    }) ?? []
  )
}

const getFollowableCompetitorsByCompetitionId = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
  competitionId: string,
): SportEntitiesData => {
  if (!sportEntities) {
    return []
  }

  if (!isFollowingEnabled) {
    return []
  }

  const prefilteredResult =
    sportEntities.followableCompetitorsByCompetitionOrderedList[competitionId]

  let finalEntityIdList: string[] = []

  if (!Array.isArray(prefilteredResult)) {
    finalEntityIdList = []
  } else if (Array.isArray(prefilteredResult)) {
    finalEntityIdList = prefilteredResult
  }

  return (
    finalEntityIdList.map((entityId) => {
      const sportEntity = sportEntities?.entitiesRegistry[entityId]

      return {
        ...sportEntity,
        ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
      }
    }) ?? []
  )
}

const getFollowableCompetitorsWithNoCompetitionId = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
): SportEntitiesData => {
  if (!sportEntities) {
    return []
  }

  if (!isFollowingEnabled) {
    return []
  }

  const prefilteredResult =
    sportEntities.followableCompetitorsWithoutCompetitionOrderedList

  let finalEntityIdList: string[] = []

  if (!Array.isArray(prefilteredResult)) {
    finalEntityIdList = []
  } else if (Array.isArray(prefilteredResult)) {
    finalEntityIdList = prefilteredResult
  }

  return (
    finalEntityIdList.map((entityId) => {
      const sportEntity = sportEntities?.entitiesRegistry[entityId]

      return {
        ...sportEntity,
        ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
      }
    }) ?? []
  )
}

const followedSportEntitySorter: Exclude<
  Parameters<Array<SportEntityType>['sort']>[0],
  undefined
> = (entityA, entityB) => entityA.order - entityB.order

export {
  formatSportEntities,
  fetchSportEntities,
  isSameSportEntity,
  getSportEntityById,
  getSportEntityBySportradarId,
  getSportEntitiesList,
  getFollowableCompetitorsByCompetitionId,
  getFollowableCompetitorsWithNoCompetitionId,
  followedSportEntitySorter,
}
